import { type FormValues } from '@uturn/portal/modules/shipments/schema';
import { ContainerStatus } from '@uturn/portal/types/shipment';
import {
	addValidationIssue,
	type ValidationResults,
	validationSuccess,
} from '@uturn/portal/types/validation';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export function useGrossWeightValidation() {
	const { t } = useTranslation();
	/**
	 * grossWeight is almost always is required, unless
	 * saving as concept or ConntainerStatus.EMPTY, which
	 * is only possible for TransportType.SHUNT.
	 *
	 * @param grossWeight
	 * @param containerStatus
	 * @param publishing Publishing or updating published shipment
	 * @param validationResults
	 * @returns
	 */
	const validateGrossWeight = (
		grossWeight: number,
		containerStatus: ContainerStatus,
		publishing: boolean,
		validationResults?: ValidationResults<FormValues>,
	): ValidationResults<FormValues> => {
		let results = validationResults ?? validationSuccess;

		if (!publishing || containerStatus === ContainerStatus.EMPTY) {
			return results;
		}

		const grossWeightSchema = z
			.number({
				required_error: t(
					'general.errors.form_validation.required',
					'Required.',
				).toString(),
				invalid_type_error: t(
					'general.errors.form_validation.nan',
					'Must be a number.',
				).toString(),
			})
			.max(99999, {
				message: t(
					'general.errors.gross_weight.too_big',
					'This is too heavy.',
				).toString(),
			})
			.positive({
				message: t(
					'general.errors.form_validation.too_small',
					'Must be greater than 0.',
				).toString(),
			});
		try {
			grossWeightSchema.parse(grossWeight);
		} catch (err) {
			if (err instanceof z.ZodError) {
				results = addValidationIssue<FormValues>(
					results,
					err.issues[0].message,
					'grossWeight.quantity',
				);
			}
		}
		return results;
	};
	return { validateGrossWeight };
}
